import cx from 'classnames';
import { FC, HTMLAttributes, ReactNode } from 'react';
import styles from './Badge.module.css';

export type BadgeVariant = 'filled' | 'light' | 'personify';
export type BadgeSize = 'L' | 'M' | 'S';
export type BadgeIconPosition = 'left' | 'right';
export type BadgeColor =
  | 'grey'
  | 'blue'
  | 'purple'
  | 'neutral'
  | 'orange'
  | 'red'
  | 'green'
  | 'navy'
  | 'yellow'
  | 'bluesky'
  | string;

type StandardBadgeVariant = Exclude<BadgeVariant, 'personify'>;

interface BaseBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode;
}

interface StandardBadgeProps extends BaseBadgeProps {
  icon?: ReactNode;
  iconPosition?: BadgeIconPosition;
  size?: BadgeSize;
  variant?: StandardBadgeVariant;
  color?: BadgeColor;
  isUppercase?: boolean;
}

interface PersonifyVariantBadgeProps extends BaseBadgeProps {
  variant: 'personify';
  icon?: ReactNode;
  iconPosition?: BadgeIconPosition;
  size?: never;
  color?: never;
  isUppercase?: never;
}

export type BadgeProps = StandardBadgeProps | PersonifyVariantBadgeProps;

const Badge: FC<BadgeProps> = props => {
  if (props.variant === 'personify') {
    const { children, icon, iconPosition = 'left', className, ...rest } = props;

    return (
      <span
        className={cx(styles.badgeContainer, className)}
        data-variant="personify"
        data-icon-position={iconPosition}
        {...rest}
      >
        {icon && (
          <span className={styles.iconWrapper} data-testid="badgeIcon">
            {icon}
          </span>
        )}
        {children}
      </span>
    );
  }

  const {
    children,
    icon,
    iconPosition = 'left',
    size = 'M',
    color = 'neutral',
    variant = 'filled',
    isUppercase = false,
    className,
    ...rest
  } = props;

  return (
    <span
      className={cx(styles.badgeContainer, className)}
      data-size={size}
      data-color={color}
      data-variant={variant}
      data-uppercase={isUppercase}
      data-icon-position={iconPosition}
      {...rest}
    >
      {icon && (
        <span className={styles.iconWrapper} data-testid="badgeIcon">
          {icon}
        </span>
      )}
      {children}
    </span>
  );
};

export default Badge;
